import { Button } from '@/components/ui/button';
import { useState, useMemo, type ReactNode } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { formatDistanceToNow } from 'date-fns';
import type { InlineContact, ContactContext, TriggerKind } from '@/lib/contactTypes';
import MessageCounterpartyDialog from './MessageCounterpartyDialog';
import ContactCardBody, { type ContactChannel } from './ContactCardBody';
import DealBrief from './DealBrief';
import { trackActivity } from '@/lib/activityTracking';
import { useHeaderData } from '@/hooks/useHeaderData';
import { useIsMobile } from '@/hooks/use-mobile';
import { appendSignature, type OutboundLinkEvidence } from '@/lib/outboundSignature';
import { composeUrl, emailButtonLabel, isMobileDevice, textUrl, whatsappUrl } from '@/lib/composeDispatch';
import { useMailClient } from '@/components/share/MailClientButton';
import { toast } from 'sonner';

interface Props {
  /**
   * Pre-resolved contact — comes inline from the RPC row that surfaced this
   * buyer. Null/undefined means we have nothing to show and the trigger renders
   * as plain text (no popover behavior).
   */
  contact: InlineContact | null | undefined;
  context?: ContactContext;
  sourceSurface?: string; // e.g. 'my_day_queue', 'insights_buyers_drilldown'
  children: ReactNode; // the name/label rendered as the trigger
  /** Controlled open state (used by deep links, e.g. an email naming a buyer). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Evidence deciding where the Cannafax signature link may point. Omit and the
   * signature falls back to the homepage — the one destination that always
   * works for a recipient who is not signed in.
   */
  linkEvidence?: OutboundLinkEvidence | null;
}

const triggerLabel = (kind?: TriggerKind, at?: string, ctx?: string): string => {
  const when = at ? formatDistanceToNow(new Date(at), { addSuffix: true }) : null;
  switch (kind) {
    case 'view':
      return `Viewed ${ctx ?? 'a listing'}${when ? ` · ${when}` : ''}`;
    case 'inquiry':
      return `Sent inquiry${ctx ? ` on ${ctx}` : ''}${when ? ` · ${when}` : ''}`;
    case 'sample_request':
      return `Requested a sample${ctx ? ` of ${ctx}` : ''}${when ? ` · ${when}` : ''}`;
    case 'share_open':
      return `Opened share link${when ? ` · ${when}` : ''}`;
    case 'outcome':
      return `Outcome recorded${when ? ` · ${when}` : ''}`;
    case 'brief_generated':
      return `Regenerated brief${when ? ` · ${when}` : ''}`;
    default:
      return when ?? '';
  }
};

/**
 * Quiet, never apologetic. A fallback contact simply states what it is rather
 * than explaining that the individual behind the action is unknown.
 */
const contextLineFor = (contact: InlineContact, context?: ContactContext): string | null => {
  if (contact.contact_source === 'trigger') {
    return triggerLabel(context?.trigger_kind, context?.trigger_at, context?.trigger_context) || null;
  }
  if (contact.contact_source === 'company_primary') return 'Company primary contact';
  return 'First person on this company';
};

export default function ContactPopover({
  contact,
  context,
  sourceSurface,
  children,
  open: openProp,
  onOpenChange,
  linkEvidence,
}: Props) {
  const [openState, setOpenState] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const { data: headerData } = useHeaderData();
  const isMobile = useIsMobile();
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };

  const e164 = useMemo(() => {
    const raw = contact?.phone?.trim();
    if (!raw) return null;
    // Already international (leading +): parse without a default region so a
    // non-US contact still gets Call / SMS / WhatsApp. Otherwise assume US.
    const parsed = raw.startsWith('+')
      ? parsePhoneNumberFromString(raw)
      : parsePhoneNumberFromString(raw, 'US');
    if (parsed?.isValid()) return parsed.number; // "+15551234567"
    // Unparseable but plausibly dialable: keep the digits so the channels stay
    // available rather than showing a dead text row.
    const digits = raw.replace(/[^0-9]/g, '');
    return digits.length >= 8 ? `+${digits}` : null;
  }, [contact?.phone]);


  // Nothing to resolve — render the label plain, no popover, no wrappers.
  if (!contact) {
    return <>{children}</>;
  }

  const digits = e164 ? e164.replace(/[^0-9]/g, '') : null;

  const emailSubject = context?.trigger_context ? `Re: ${context.trigger_context}` : 'Following up';

  // Your own teammate never needs the Cannafax line.
  const isTeammate = !!headerData?.companyId && headerData.companyId === contact.company_id;
  const intro = context?.trigger_context ? `Re: ${context.trigger_context}` : '';
  const sigOptions = { skip: isTeammate, evidence: linkEvidence };

  const emailBody = appendSignature('email', intro, sigOptions);
  const smsBody = appendSignature('sms', intro, sigOptions);
  const waText = appendSignature('whatsapp', intro, sigOptions);

  const logClick = (channel: string) => {
    try {
      trackActivity('contact_action_clicked', {
        channel,
        target_user_id: contact?.user_id ?? null,
        target_company_id: contact.company_id ?? null,
        trigger_kind: context?.trigger_kind ?? null,
        source_surface: sourceSurface ?? null,
      });
    } catch {
      /* non-blocking */
    }
  };

  // One dispatcher for every person: the user's own Gmail/Outlook/Mail app,
  // native Messages only on phones (desktop has no sms handler), WhatsApp everywhere.
  const mailClient = useMailClient();
  const emailHref = contact.email
    ? composeUrl(mailClient, { to: [contact.email], subject: emailSubject, body: emailBody })
    : null;
  const smsHref = e164 ? textUrl(smsBody, e164) : null;

  const channels: ContactChannel[] = [
    ...(emailHref
      ? [{ key: 'email' as const, label: emailButtonLabel(mailClient), href: emailHref, external: emailHref.startsWith('http') }]
      : []),
    ...(e164 ? [{ key: 'phone' as const, label: 'Call', href: `tel:${e164}` }] : []),
    ...(smsHref ? [{ key: 'sms' as const, label: 'Text message', href: smsHref }] : []),
    ...(digits
      ? [{ key: 'whatsapp' as const, label: 'WhatsApp', href: whatsappUrl(waText, digits), external: true }]
      : []),
  ];

  const card = (dense: boolean) => (
    <ContactCardBody
      contact={contact}
      contextLine={contextLineFor(contact, context)}
      channels={channels}
      dense={dense}
      onMessage={
        contact.company_id
          ? () => {
              logClick('in_app');
              setMsgOpen(true);
              setOpen(false);
            }
          : undefined
      }
      onChannelClick={(key) => {
        logClick(key);
        if (key === 'email' && emailBody) {
          try { void navigator.clipboard?.writeText(`${emailSubject}\n\n${emailBody}`); } catch { /* ignore */ }
          toast.info(`Opening ${emailButtonLabel(mailClient).replace('Email in ', '')}…`, {
            description: mailClient === 'app' && !isMobileDevice()
              ? 'Your message is copied. If nothing opens, pick Gmail or Outlook on any Email button.'
              : 'Your message is also copied, in case it doesn’t open.',
          });
        }
      }}
      footer={
        contact.company_id && !isTeammate && headerData?.companyId
          ? <DealBrief counterpartyCompanyId={contact.company_id} counterpartyName={contact.company_name} />
          : undefined
      }
    />
  );

  const trigger = (
    <Button variant="quiet" size="content"
      type="button"
      className="inline whitespace-normal rounded-none text-inherit font-normal text-left hover:underline underline-offset-2 decoration-dotted cursor-pointer bg-transparent p-0 border-0 font-inherit"
      onClick={(e) => {
        e.stopPropagation();
        if (isMobile) setOpen(true);
      }}
    >
      {children}
    </Button>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent onClick={(e) => e.stopPropagation()}>
            <div className="px-4 pb-8 pt-2">{card(false)}</div>
          </DrawerContent>
        </Drawer>

        <MessageCounterpartyDialog
          open={msgOpen}
          onOpenChange={setMsgOpen}
          recipientCompanyId={contact.company_id}
          recipientName={contact.full_name}
          recipientCompanyName={contact.company_name ?? ''}
          listingId={context?.listing_id ?? null}
          contextLabel={context?.trigger_context}
        />
      </>
    );
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-3" onClick={(e) => e.stopPropagation()}>
          {card(true)}
        </PopoverContent>
      </Popover>

      <MessageCounterpartyDialog
        open={msgOpen}
        onOpenChange={setMsgOpen}
        recipientCompanyId={contact.company_id}
        recipientName={contact.full_name}
        recipientCompanyName={contact.company_name ?? ''}
        listingId={context?.listing_id ?? null}
        contextLabel={context?.trigger_context}
      />
    </>
  );
}
